import { BookingModel, Outbound2 } from 'ts/dynamic/interfaces/DynamicBookResponse';
import { DynamicEventResponse, DynamicFlight, Outbound } from 'ts/dynamic/interfaces/DynamicResponse';
import i18n from 'i18next';
import { DynTransfer, TransferView } from 'ts/dynamic/interfaces/DynamicTransferUpsell';
import { getCurrency } from 'utils/utils';

export const getNoBaggage = (bound: Outbound) => {
  return (
    !!bound.listOfFlyBaggagePrice?.length &&
    bound.listOfFlyBaggagePrice[0].maxTotalWeight === 0 &&
    bound.listOfFlyBaggagePrice[0].pieces === 0
  );
};

export const getBaggageTooltipContent = (noBaggage: boolean, outbond: Outbound) => {
  if (!outbond.listOfFlyBaggagePrice || !outbond.listOfFlyBaggagePrice.length) {
    return '';
  } else if (!noBaggage && outbond.listOfFlyBaggagePrice[0].pieces === 1) {
    return i18n.t('flight.airline_allow_one');
  } else if (!noBaggage && outbond.listOfFlyBaggagePrice[0].pieces > 1) {
    return i18n.t('flight.airline_allow_multiple', {
      count: outbond.listOfFlyBaggagePrice[0].pieces,
    });
  } else if (!noBaggage && outbond.listOfFlyBaggagePrice[0].maxTotalWeight > 0) {
    const val =
      outbond.listOfFlyBaggagePrice[0].maxTotalWeight +
      outbond.listOfFlyBaggagePrice[0].maxTotalWeightUnit;
    return i18n.t('flight.airline_allow_max', { weight: val });
  }
  return '';
};

export const getSortedFlights = (
  flights: DynamicFlight[] | undefined,
  sort: string
): DynamicFlight[] => {
  if (flights?.length) {
    switch (sort) {
      case 'priceIncrease':
        return flights.sort((a, b) => a.priceGrossAndTax - b.priceGrossAndTax);
      case 'priceDecrease':
        return flights.sort((a, b) => b.priceGrossAndTax - a.priceGrossAndTax);
      case 'airlineIncrease':
        return flights.sort((a, b) => a.airlineCode.localeCompare(b.airlineCode));
      case 'airlineDecrease':
        return flights.sort((a, b) => b.airlineCode.localeCompare(a.airlineCode));
      case 'durationIncrease':
        return flights.sort((a, b) => a.outbound.elapsedDTime - b.outbound.elapsedDTime);
      case 'durationDecrease':
        return flights.sort((a, b) => b.outbound.elapsedDTime - a.outbound.elapsedDTime);
      case 'departureOutboundIncrease':
        return flights.sort(
          (a, b) =>
            new Date(a.outbound.departureDateTime).getTime() -
            new Date(b.outbound.departureDateTime).getTime()
        );
      case 'departureOutboundDecrease':
        return flights.sort(
          (a, b) =>
            new Date(b.outbound.departureDateTime).getTime() -
            new Date(a.outbound.departureDateTime).getTime()
        );
      case 'returningOutboundIncrease':
        return flights.sort(
          (a, b) =>
            new Date(a.outbound.arrivalDateTime).getTime() -
            new Date(b.outbound.arrivalDateTime).getTime()
        );
      case 'returningOutboundDecrease':
        return flights.sort(
          (a, b) =>
            new Date(b.outbound.arrivalDateTime).getTime() -
            new Date(a.outbound.arrivalDateTime).getTime()
        );
      case 'departureInboundIncrease':
        return flights.sort(
          (a, b) =>
            new Date(a.inbound?.departureDateTime).getTime() -
            new Date(b.inbound?.departureDateTime).getTime()
        );
      case 'departureInboundDecrease':
        return flights.sort(
          (a, b) =>
            new Date(b.inbound?.departureDateTime).getTime() -
            new Date(a.inbound?.departureDateTime).getTime()
        );
      case 'returningInboundIncrease':
        return flights.sort(
          (a, b) =>
            new Date(a.inbound?.arrivalDateTime).getTime() -
            new Date(b.inbound?.arrivalDateTime).getTime()
        );
      case 'returningInboundDecrease':
        return flights.sort(
          (a, b) =>
            new Date(b.inbound?.arrivalDateTime).getTime() -
            new Date(a.inbound?.arrivalDateTime).getTime()
        );

      default:
        return flights;
    }
  }

  return [];
};

export const formatPriceDynamic = (price: number, currencySign: string) => {
  const language = localStorage.getItem('language') || 'he';
  const isRtl = language === 'he';
  if (price === 0) {
    return `0 ${currencySign}`;
  } else if (price < 0 && isRtl) {
    return `${Math.abs(Math.round(price)).toLocaleString()}-${currencySign}`;
  } else if (price > 0 && isRtl) {
    return `${Math.round(price).toLocaleString()}+${currencySign}`;
  } else if (price < 0) {
    return `-${Math.abs(Math.round(price)).toLocaleString()} ${currencySign}`;
  } else if (price > 0) {
    return `+${Math.round(price).toLocaleString()} ${currencySign}`;
  }
  return '';
};

export const getDynPassengerValidation = (index: number) => {
  const baseValidation = {
    firstName: { required: true, minLength: 2, maxLength: 20, english: true },
    lastName: { required: true, minLength: 2, maxLength: 20, english: true },
    dob: {},
    phone: {},
    email: {},
  };
  if (index === 0) {
    return {
      ...baseValidation,
      email: { required: true, email: true },
      phone: { required: true },
    };
  } else {
    return baseValidation;
  }
};

export const getStopsText = (bound: Outbound2) => {
  if (bound.gdsFlyAirs.length === 1) {
    return i18n.t('filter.direct_flight');
  } else if (bound.gdsFlyAirs.length === 2) {
    return i18n.t('filter.one_stop');
  } else {
    return `${bound.gdsFlyAirs.length - 1} ${i18n.t('filter.stops_sm')}`;
  }
};

export const getTransferValue = (item?: DynTransfer) => {
  if (!item) return undefined;
  let vehicle: TransferView = {} as TransferView;
  if (item.transfer?.transferVehicles.length) {
    vehicle.personCount = item.transfer.adult + item.transfer.cnn;
    vehicle.desc = item.transfer?.transferVehicles[0]?.vehicle.desc;
    vehicle.maxLuggage = item.transfer?.transferVehicles[0]?.vehicle.maxLuggage;
    vehicle.maxPassengers = item.transfer?.transferVehicles[0]?.vehicle.maxPassengers;
    vehicle.price = item.transfer?.transferVehicles[0]?.itemPrice.gross.value;
    vehicle.pricePerPerson =
      item.transfer?.transferVehicles[0]?.itemPrice.gross.value / vehicle.personCount;
    vehicle.duration = +item.transfer?.transferVehicles[0]?.transferDuration.tranTime;
    vehicle.currencySign = getCurrency(
      item.transfer?.transferVehicles[0]?.itemPrice.gross.currency
    );
    vehicle.code = +item.transfer?.transferVehicles[0]?.vehicle.transferVehicleTypeCode;
    vehicle.priceKey = item.priceKey;
  }
  return vehicle;
};

export const getElapsedStr = (minutes?: number) => {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours === 0
    ? `${mins}${i18n.t('minutes_sm')}`
    : `${hours}${i18n.t('hours_sm')} ${mins}${i18n.t('minutes_sm')}`;
};

export const getDescText = (model: BookingModel) => {
  if (model.eventInfo && model.flightInfo && model.hotelInfo) {
    return i18n.t('dyn.fl_htl_ticket');
  } else if (model.hotelInfo && model.flightInfo) {
    return i18n.t('dyn.fl_pl_htl');
  } else if (model.eventInfo) {
    return i18n.t('dyn.event');
  }
};

export const getDynText = (model: DynamicEventResponse) => {
  if (model.dynamicAttraction && model.dynamicFlight && model.dynamicHotel) {
    return i18n.t('dyn.fl_htl_ticket');
  } else if (model.dynamicHotel && model.dynamicFlight) {
    return i18n.t('dyn.fl_pl_htl');
  } else if (model.dynamicAttraction) {
    return i18n.t('dyn.event');
  }
};
export const ss = 1;
